// Security update: HTML rendered from stored content is sanitized with sanitizeHtml() before it is inserted into the page.
/**
 * Blog Preview Client Component
 *
 * Displays preview of potential blogs (not yet published) using generated content from N8N
 * - Fetches from potential_blogs table using generated_slug
 * - Shows dismissible preview banner
 * - Includes noindex/nofollow meta tags to prevent search engine indexing
 * - Does not increment view count (preview only)
 * - Accessible to anyone with the link
 */

import { sanitizeHtml } from '@/lib/sanitize-html';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';
import { getBlogImageUrl } from '@/lib/image-url';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Loader as Loader2, X, Eye } from 'lucide-react';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true,
  pedantic: false,
});

// Configure marked renderer to add target="_blank" to all links
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link.bind(renderer);
renderer.link = (href: string, title: string | null | undefined, text: string) => {
  const html = originalLinkRenderer(href, title, text);
  return html.replace('<a', '<a target="_blank" rel="noopener noreferrer"');
};
marked.use({ renderer });

interface PotentialBlog {
  id: string;
  title: string;
  slug: string | null;
  description: string;
  thumbnail_storage_path?: string;
  generated_content: string;
  created_at: string;
  status: string;
}

export default function BlogPreviewClient() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<PotentialBlog | null>(null);
  const [loading, setLoading] = useState(true);
  const [readProgress, setReadProgress] = useState(0);
  const [showBanner, setShowBanner] = useState(true);

  // Check localStorage for banner dismissal on mount
  useEffect(() => {
    if (slug) {
      const dismissed = localStorage.getItem(`preview-banner-dismissed-${slug}`);
      setShowBanner(!dismissed);
    }
  }, [slug]);

  const htmlContent = useMemo(() => {
    if (!blog?.generated_content) return '';
    try {
      let content = blog.generated_content;
      content = content.replace(/(<\/[^>]+>)\n(?!\n)/g, '$1\n\n');
      content = content.replace(/(<\/[^>]+>)([^\n<])/g, '$1\n\n$2');
      return sanitizeHtml(marked.parse(content) as string);
    } catch (error) {
      return blog.generated_content;
    }
  }, [blog?.generated_content]);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;

      const scrollableHeight = documentHeight - windowHeight;
      const progress = (scrollTop / scrollableHeight) * 100;

      setReadProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!slug) {
        return;
      }

      try {
        setLoading(true);

        const { data, error } = await supabase
          .from('potential_blogs')
          .select(
            'id, title, description, thumbnail_storage_path, status, generated_content, published_blog_id, created_at, updated_at, keywords, related_products, slug',
          )
          .eq('slug', slug)
          .not('generated_content', 'is', null)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          navigate('/blogs');
          return;
        }

        setBlog(data);
      } catch (error) {
        navigate('/blogs');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, navigate]);

  const handleDismissBanner = () => {
    if (slug) {
      localStorage.setItem(`preview-banner-dismissed-${slug}`, 'true');
      setShowBanner(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <ContactBar />
        <Header />
        <div className="flex items-center justify-center pt-0 pb-">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white">
        <ContactBar />
        <Header />
        <div className="flex flex-col items-center justify-center pt-0 pb-">
          <h1 className="text-4xl font-bold text-jv-dark mb-4">Preview niet gevonden</h1>
          <Link to="/blogs" className="text-jv-primary hover:text-jv-accent">
            Terug naar blogs
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const getImageUrl = (storagePath?: string) => {
    if (storagePath) {
      return getBlogImageUrl(storagePath, 'large');
    }
    return 'https://buitnplanten.nl/assets/sections/blogs.png';
  };

  const pageTitle = `[PREVIEW] ${blog.title} | Buitn Planten`;
  const pageDescription = blog.description;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Prevent search engine indexing */}
        <meta name="robots" content="noindex, nofollow, noarchive, nocache" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="googlebot-news" content="noindex, nofollow" />

        {/* No Open Graph or Twitter cards for previews */}
      </Helmet>

      <ContactBar />
      <Header />

      {/* Preview Banner */}
      {showBanner && (
        <div className="fixed top-[88px] left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 flex-shrink-0" />
                <p className="font-semibold text-sm sm:text-base">
                  PREVIEW - Deze blog is nog niet gepubliceerd en is alleen zichtbaar via deze link
                </p>
              </div>
              <button
                onClick={handleDismissBanner}
                className="ml-4 p-1 hover:bg-white/20 rounded-full transition-colors duration-200 flex-shrink-0"
                aria-label="Sluit banner"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Read Progress Bar */}
      <div className={`fixed ${showBanner ? 'top-[136px]' : 'top-[88px]'} left-0 right-0 h-[2px] bg-gray-200 z-40 transition-all duration-300`}>
        <div
          className="h-full bg-jv-primary transition-all duration-150 ease-out"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <main className={`${showBanner ? 'pt-0' : 'pt-0'} pb-16 bg-white transition-all duration-300`}>
        <article className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16">
          {/* Back Button */}
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-jv-primary hover:text-jv-accent transition-colors duration-300 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="font-semibold">Terug naar blogs</span>
          </Link>

          {/* Meta Information */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(blog.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{estimateReadTime(blog.generated_content)} min lezen</span>
            </div>
            <div className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold uppercase">
              Preview
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-jv-primary mb-6 leading-tight">
            {blog.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            {blog.description}
          </p>

          {/* Featured Image */}
          <div className="relative h-[400px] md:h-[500px] rounded-tl-[72px] rounded-br-[72px] overflow-hidden bg-jv-tertiary shadow-lg mb-12">
            <img
              src={getImageUrl(blog.thumbnail_storage_path)}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Blog Content */}
          <div
            className="prose prose-lg max-w-none prose-headings:text-jv-primary prose-headings:font-bold prose-headings:mb-4 prose-headings:mt-8 prose-h2:text-4xl prose-h2:leading-tight prose-h3:text-2xl prose-h3:leading-snug prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-a:text-jv-accent prose-a:no-underline hover:prose-a:underline prose-strong:text-jv-primary prose-strong:font-semibold prose-ul:text-gray-700 prose-ul:my-6 prose-ol:text-gray-700 prose-ol:my-6 prose-li:my-2 prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          {/* Back Button at Bottom */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-jv-primary hover:text-jv-accent transition-colors duration-300 group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
              <span className="font-semibold">Terug naar alle blogs</span>
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
